import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RequestFieldModal = ({ open, onOpenChange, operationId, missingLabels = [], onSent }) => {
  const [selected, setSelected] = useState([]);
  const [targets, setTargets] = useState(['field_ops', 'operator']);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setSelected(missingLabels); setNote(''); setTargets(['field_ops', 'operator']); } }, [open]); // eslint-disable-line
  const uniqueLabels = Array.from(new Set(missingLabels));

  const toggle = (label) => setSelected((p) => p.includes(label) ? p.filter((x) => x !== label) : [...p, label]);
  const toggleTarget = (t) => setTargets((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);

  const submit = async () => {
    if (!selected.length) { toast.error('Sélectionnez au moins un document'); return; }
    if (!targets.length) { toast.error('Sélectionnez au moins un destinataire'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/operations/${operationId}/documents/request-field`, {
        documentLabels: selected, note: note || null, targets,
      });
      toast.success(r.data?.message || 'Demande envoyée');
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Envoi impossible');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="request-field-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-emerald-600" /> Demander au terrain</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="mb-2 block">Documents à demander</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {missingLabels.length === 0 && <p className="text-sm text-gray-400">Aucun document manquant.</p>}
              {uniqueLabels.map((l) => (
                <label key={l} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={selected.includes(l)} onCheckedChange={() => toggle(l)} data-testid={`req-doc-${l}`} />
                  {l}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Destinataires</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={targets.includes('field_ops')} onCheckedChange={() => toggleTarget('field_ops')} /> Field Ops assigné
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={targets.includes('operator')} onCheckedChange={() => toggleTarget('operator')} /> Opérateur assigné
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Message (optionnel)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Précisions pour le terrain…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-field-request-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />} Envoyer la demande
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequestFieldModal;
